/**
 * InsightPro™ — AI Business Advisor  v1
 * Full-screen chat with live BigQuery context injected per message
 * Route: /advisor
 */
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { auth } from '../utils/firebase';

const API_BASE = 'https://africa-south1-insightprofinal-3c793.cloudfunctions.net/dataApi';

// ── Suggested prompts ───────────────────────────────────────────
const SUGGESTIONS = [
  'Which branch needs the most attention this month?',
  'Should I be worried about my debtors book?',
  'Where are my biggest margin opportunities?',
  'Which products are draining cash in dead stock?',
  'What would a 2% margin improvement be worth annually?',
  'Which branch is punching above its weight?',
];

import { pbiQuery } from '../utils/api';

// ── Period → date range ──────────────────────────────────────
function getDateRange(period) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const y = today.getFullYear(), m = today.getMonth()+1, d = today.getDate();
  const dow = today.getDay();
  let startDate, endDate;
  if (period === 'TODAY') {
    startDate = `${y},${m},${d}`; endDate = `${y},${m},${d}`;
  } else if (period === 'YESTERDAY' || period === 'YEST') {
    const yest = new Date(today); yest.setDate(yest.getDate()-1);
    const yy=yest.getFullYear(),ym=yest.getMonth()+1,yd=yest.getDate();
    startDate=`${yy},${ym},${yd}`; endDate=`${yy},${ym},${yd}`;
  } else if (period === 'WTD') {
    const s = new Date(today); s.setDate(today.getDate()-(dow===0?6:dow-1));
    startDate=`${s.getFullYear()},${s.getMonth()+1},${s.getDate()}`; endDate=`${y},${m},${d}`;
  } else if (period === 'MTD') {
    startDate=`${y},${m},1`; endDate=`${y},${m},${d}`;
  } else if (period === 'QTD') {
    startDate=`${y},${Math.floor((m-1)/3)*3+1},1`; endDate=`${y},${m},${d}`;
  } else if (period === 'YTD') {
    startDate=`${y},1,1`; endDate=`${y},${m},${d}`;
  } else {
    startDate=`${y},${m},1`; endDate=`${y},${m},${d}`;
  }
  const [sy,sm,sd] = startDate.split(',').map(Number);
  const [ey,em,ed] = endDate.split(',').map(Number);
  return { startDate, endDate, pyStart:`${sy-1},${sm},${sd}`, pyEnd:`${ey-1},${em},${ed}` };
}

// ── Build context from live PBI data ────────────────────────
async function buildContext(period) {
  const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);
  const cyF = `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
  const pyF = `'Transactions'[TXDATE] >= DATE(${pyStart})  && 'Transactions'[TXDATE] <= DATE(${pyEnd})`;
  const now  = new Date();
  const ytdF = `'Transactions'[TXDATE] >= DATE(${now.getFullYear()},1,1) && 'Transactions'[TXDATE] <= DATE(${now.getFullYear()},${now.getMonth()+1},${now.getDate()})`;
  const fmtR = (n) => n == null ? 'N/A' : `R${(n/1000000).toFixed(2)}M`;
  const fmtK = (n) => n == null ? 'N/A' : `R${(n/1000).toFixed(0)}K`;
  const pct  = (n) => n == null ? 'N/A' : `${(Number(n)*100).toFixed(1)}%`;
  const sign = (n) => (n != null && n >= 0) ? '+' : '';
  try {
    const [
      kpiRows, branchRows, debtorRows, slowRows, deptRows,
      allBranchRows, debtorAcctRows, topProdRows, botProdRows
    ] = await Promise.all([

      // 1. Overall KPIs
      pbiQuery({ dax: `EVALUATE ROW(
        "Revenue",      CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
        "LastYear",     CALCULATE(SUM('Transactions'[SALES]),      ${pyF}),
        "Purchases",    CALCULATE(SUM('Transactions'[PURCHASES]),  ${cyF}),
        "Units",        CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}),
        "StockValue",   SUMX(FILTER('StockBalances','StockBalances'[ONHAND]>0),'StockBalances'[ONHAND]*RELATED('Stock'[AVRGCOST])),
        "StockLines",   COUNTROWS(FILTER('Stock','Stock'[ONHAND]>0)),
        "ZeroLines",    COUNTROWS(FILTER('Stock','Stock'[ONHAND]=0)),
        "YTDRevenue",   CALCULATE(SUM('Transactions'[SALES]),      ${ytdF}),
        "YTDPurchases", CALCULATE(SUM('Transactions'[PURCHASES]),  ${ytdF}),
        "YTDDays",      DATEDIFF(DATE(${now.getFullYear()},1,1),TODAY(),DAY)
      )` }).then(r => r?.rows || []),

      // 2. Top 10 branches
      pbiQuery({ dax: `EVALUATE
        TOPN(10, ADDCOLUMNS(
          SUMMARIZE(FILTER('Transactions',${cyF}),'CompanyList'[COMPANY NAME]),
          "Revenue",    CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
          "LastYear",   CALCULATE(SUM('Transactions'[SALES]),      ${pyF}),
          "Growth",     DIVIDE(CALCULATE(SUM('Transactions'[SALES]),${cyF})-CALCULATE(SUM('Transactions'[SALES]),${pyF}),CALCULATE(SUM('Transactions'[SALES]),${pyF})),
          "Purchases",  CALCULATE(SUM('Transactions'[PURCHASES]),  ${cyF}),
          "Units",      CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}),
          "StockValue", CALCULATE(SUMX('StockBalances','StockBalances'[ONHAND]*RELATED('Stock'[AVRGCOST])))
        ),[Revenue],DESC) ORDER BY [Revenue] DESC` }).then(r => r?.rows || []),

      // 3. Debtors summary
      pbiQuery({ dax: `EVALUATE ROW(
        "TotalBook",      CALCULATE(SUM('Debtors'[TOTAL]),   'Debtors'[TOTAL]>0),
        "Current",        CALCULATE(SUM('Debtors'[CURRENT]), 'Debtors'[TOTAL]>0),
        "Days30",         CALCULATE(SUM('Debtors'[DAYS_30]), 'Debtors'[TOTAL]>0),
        "Days60",         CALCULATE(SUM('Debtors'[DAYS_60]), 'Debtors'[TOTAL]>0),
        "Days90",         CALCULATE(SUM('Debtors'[DAYS_90]), 'Debtors'[TOTAL]>0),
        "Days120",        CALCULATE(SUM('Debtors'[DAYS_120]),'Debtors'[TOTAL]>0),
        "Days150",        CALCULATE(SUM('Debtors'[DAYS_150]),'Debtors'[TOTAL]>0),
        "Days180",        CALCULATE(SUM('Debtors'[DAYS_180]),'Debtors'[TOTAL]>0),
        "Overdue60",      CALCULATE(SUM('Debtors'[DAYS_60])+SUM('Debtors'[DAYS_90])+SUM('Debtors'[DAYS_120])+SUM('Debtors'[DAYS_150])+SUM('Debtors'[DAYS_180]),'Debtors'[TOTAL]>0),
        "ActiveAccounts", CALCULATE(COUNTROWS('Debtors'),'Debtors'[TOTAL]>0),
        "OnHoldCount",    CALCULATE(COUNTROWS('Debtors'),'Debtors'[ONHOLD]="Y",'Debtors'[TOTAL]>0),
        "OnHoldValue",    CALCULATE(SUM('Debtors'[TOTAL]),'Debtors'[ONHOLD]="Y",'Debtors'[TOTAL]>0),
        "CreditLimit",    CALCULATE(SUM('Debtors'[CRLIMIT]),'Debtors'[TOTAL]>0)
      )` }).then(r => r?.rows || []),

      // 4. Slow movers
      pbiQuery({ dax: `EVALUATE
        TOPN(8,
          ADDCOLUMNS(
            FILTER('Stock', 'Stock'[ONHAND] > 0 && 'Stock'[AVRGCOST] > 0),
            "StockValue", 'Stock'[ONHAND] * 'Stock'[AVRGCOST],
            "MTDSales",   CALCULATE(SUM('Transactions'[SALES]), ${cyF})
          ),
          [StockValue], DESC
        )` }).then(r => (r?.rows || []).filter(s => !s['[MTDSales]'] || s['[MTDSales]'] === 0).slice(0,8)),

      // 5. ALL departments — no limit
      pbiQuery({ dax: `EVALUATE
        ADDCOLUMNS(
          SUMMARIZE(FILTER('Transactions',${cyF}),'Departments'[DeptName]),
          "Revenue",   CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
          "LastYear",  CALCULATE(SUM('Transactions'[SALES]),      ${pyF}),
          "Purchases", CALCULATE(SUM('Transactions'[PURCHASES]),  ${cyF}),
          "Units",     CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF})
        )
        ORDER BY [Revenue] DESC` }).then(r => r?.rows || []),

      // 6. ALL branches — no limit
      pbiQuery({ dax: `EVALUATE
        ADDCOLUMNS(
          SUMMARIZE(FILTER('Transactions',${cyF}),'CompanyList'[COMPANY NAME]),
          "Revenue",    CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
          "LastYear",   CALCULATE(SUM('Transactions'[SALES]),      ${pyF}),
          "Growth",     DIVIDE(CALCULATE(SUM('Transactions'[SALES]),${cyF})-CALCULATE(SUM('Transactions'[SALES]),${pyF}),CALCULATE(SUM('Transactions'[SALES]),${pyF})),
          "Purchases",  CALCULATE(SUM('Transactions'[PURCHASES]),  ${cyF}),
          "Units",      CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}),
          "StockValue", CALCULATE(SUMX('StockBalances','StockBalances'[ONHAND]*RELATED('Stock'[AVRGCOST])))
        )
        ORDER BY [Revenue] DESC` }).then(r => r?.rows || []),

      // 7. ALL debtor accounts — no limit
      pbiQuery({ dax: `EVALUATE
        FILTER(
          SELECTCOLUMNS('Debtors',
            "Account",   'Debtors'[ACCOUNT],
            "Name",      'Debtors'[NAME],
            "Total",     'Debtors'[TOTAL],
            "Current",   'Debtors'[CURRENT],
            "Days30",    'Debtors'[DAYS_30],
            "Days60",    'Debtors'[DAYS_60],
            "Days90",    'Debtors'[DAYS_90],
            "Days120",   'Debtors'[DAYS_120],
            "Days150",   'Debtors'[DAYS_150],
            "Days180",   'Debtors'[DAYS_180],
            "Overdue60", 'Debtors'[DAYS_60]+'Debtors'[DAYS_90]+'Debtors'[DAYS_120]+'Debtors'[DAYS_150]+'Debtors'[DAYS_180],
            "OnHold",    'Debtors'[ONHOLD],
            "CrLimit",   'Debtors'[CRLIMIT],
            "LastPay",   'Debtors'[DATELASTPAY],
            "LastAmt",   'Debtors'[AMOUNTLASTPAYMENT]
          ),
          [Total] > 0
        )
        ORDER BY [Overdue60] DESC` }).then(r => r?.rows || []),

      // 8. Top 100 products by revenue
      pbiQuery({ dax: `EVALUATE
        TOPN(100, ADDCOLUMNS(
          SUMMARIZE(FILTER('Transactions',${cyF}),'Transactions'[CODE],'Stock'[Description]),
          "Revenue",  CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
          "Units",    CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}),
          "LastYear", CALCULATE(SUM('Transactions'[SALES]),      ${pyF})
        ),[Revenue],DESC)
        ORDER BY [Revenue] DESC` }).then(r => r?.rows || []),

      // 9. Top 50 slow movers by stock value — uses StockBalances[ONHAND]
      pbiQuery({ dax: `EVALUATE
        TOPN(50, ADDCOLUMNS(
          FILTER('Stock', 'Stock'[AVRGCOST] > 0),
          "TotalOnHand", CALCULATE(SUM('StockBalances'[ONHAND])),
          "StockVal",    CALCULATE(SUM('StockBalances'[ONHAND])) * 'Stock'[AVRGCOST],
          "PeriodSales", CALCULATE(SUM('Transactions'[SALES]), ${cyF})
        ),[StockVal],DESC)` }).then(r => (r?.rows||[]).filter(s => (s['[TotalOnHand]']||0) > 0 && (!s['[PeriodSales]'] || s['[PeriodSales]']===0))),
    ]);

    const kpi    = Array.isArray(kpiRows) ? kpiRows[0] : kpiRows;
    const debtor = Array.isArray(debtorRows) ? debtorRows[0] : debtorRows;
    const rev      = kpi?.['[Revenue]'];
    const lastYr   = kpi?.['[LastYear]'];
    const purch    = kpi?.['[Purchases]'];
    const growth   = rev && lastYr ? (rev-lastYr)/lastYr : null;
    const margin   = rev && purch  ? (rev-purch)/rev     : null;
    const stockVal = kpi?.['[StockValue]'];
    const ytdPurch = kpi?.['[YTDPurchases]'];
    const ytdDays  = kpi?.['[YTDDays]'] || 1;
    const ytdRev   = kpi?.['[YTDRevenue]'];
    const daysOnHand = stockVal && ytdPurch ? Math.round(stockVal/((ytdPurch/ytdDays)*365/365)) : null;
    const totalBook  = debtor?.['[TotalBook]'];
    const overdue60  = debtor?.['[Overdue60]'];
    const overduePct = totalBook > 0 ? ((overdue60/totalBook)*100).toFixed(1) : null;

    // Compressed pipe-delimited format — ~60% fewer tokens, same information
    const C  = (n) => n == null ? '-' : Math.abs(Number(n)) >= 1e6 ? `R${(n/1e6).toFixed(2)}M` : Math.abs(Number(n)) >= 1e3 ? `R${(n/1e3).toFixed(0)}K` : `R${Number(n).toFixed(0)}`;
    const Pn = (n) => n == null ? '-' : `${(Number(n)*100).toFixed(1)}%`;
    const Gn = (n) => n == null ? '-' : `${Number(n)>=0?'+':''}${(Number(n)*100).toFixed(1)}%`;
    const Nn = (n) => n == null ? '-' : Number(n).toLocaleString('en-ZA');
    const S  = (s) => (s||'').replace(/\|/g, '/');

    const lines = [];
    lines.push(`INSIGHTPRO_DATA|PERIOD:${period}|TS:${new Date().toISOString().slice(0,16)}`);
    lines.push(`NOTE:OnHand=StockBalances[ONHAND] authoritative. Stock[ONHAND] unreliable. StockVal=StockBalances[ONHAND]*Stock[AVRGCOST]`);

    // KPIs — single line
    lines.push(`KPI|REV:${C(rev)}|PY:${C(lastYr)}|GROWTH:${Gn(growth)}|MARGIN:${margin!=null?(margin*100).toFixed(1)+'%':'-'}|UNITS:${Nn(kpi?.['[Units]'])}|YTD:${C(ytdRev)}|STOCK_VAL:${C(stockVal)}|DAYS_OH:${daysOnHand??'-'}|ACTIVE_LINES:${Nn(kpi?.['[StockLines]'])}|ZERO_LINES:${Nn(kpi?.['[ZeroLines]'])}`);

    // Branches: #|NAME|REV|PY|GROWTH|MARGIN|UNITS|STOCK_VAL
    lines.push(`BRANCHES(${allBranchRows.length}):#|NAME|REV|PY|GROWTH|MARGIN|UNITS|STOCK_VAL`);
    allBranchRows.forEach((b,i) => {
      const g = b['[Growth]'];
      const m = b['[Revenue]']&&b['[Purchases]'] ? (b['[Revenue]']-b['[Purchases]'])/b['[Revenue]'] : null;
      lines.push(`${i+1}|${S(b['CompanyList[COMPANY NAME]'])}|${C(b['[Revenue]'])}|${C(b['[LastYear]'])}|${Gn(g)}|${Pn(m)}|${Nn(b['[Units]'])}|${C(b['[StockValue]'])}`);
    });

    // Depts: #|NAME|REV|PY|GROWTH|MARGIN|UNITS
    lines.push(`DEPTS(${deptRows.length}):#|NAME|REV|PY|GROWTH|MARGIN|UNITS`);
    deptRows.forEach((d,i) => {
      const m = d['[Revenue]']&&d['[Purchases]'] ? (d['[Revenue]']-d['[Purchases]'])/d['[Revenue]'] : null;
      const g = d['[Revenue]']&&d['[LastYear]'] ? (d['[Revenue]']-d['[LastYear]'])/d['[LastYear]'] : null;
      lines.push(`${i+1}|${S(d['Departments[DeptName]'])}|${C(d['[Revenue]'])}|${C(d['[LastYear]'])}|${Gn(g)}|${Pn(m)}|${Nn(d['[Units]'])}`);
    });

    // Products top 100: #|CODE|DESC|REV|UNITS|YOY
    lines.push(`PRODUCTS_TOP100(${topProdRows.length}):#|CODE|DESC|REV|UNITS|YOY`);
    topProdRows.forEach((p,i) => {
      const g = p['[Revenue]']&&p['[LastYear]'] ? (p['[Revenue]']-p['[LastYear]'])/p['[LastYear]'] : null;
      lines.push(`${i+1}|${p['Transactions[CODE]']}|${S(p['Stock[Description]']||p['Transactions[CODE]'])}|${C(p['[Revenue]'])}|${Nn(p['[Units]'])}|${Gn(g)}`);
    });

    // Debtors summary — single line
    lines.push(`DEBTORS_SUMMARY|BOOK:${C(totalBook)}|ACCTS:${debtor?.['[ActiveAccounts]']??'-'}|CRLIMIT:${C(debtor?.['[CreditLimit]'])}|CURR:${C(debtor?.['[Current]'])}|30D:${C(debtor?.['[Days30]'])}|60D:${C(debtor?.['[Days60]'])}|90D:${C(debtor?.['[Days90]'])}|120D:${C(debtor?.['[Days120]'])}|150D:${C(debtor?.['[Days150]'])}|180D:${C(debtor?.['[Days180]'])}|OVD60:${C(overdue60)}(${overduePct??'-'}%)|ONHOLD:${debtor?.['[OnHoldCount]']??'-'}accts/${C(debtor?.['[OnHoldValue]'])}`);

    // All debtor accounts: #|ACCT|NAME|TOTAL|OVD60|180D|HOLD|CRLIMIT|LASTPAY|LASTAMT
    lines.push(`DEBTOR_ACCOUNTS(${debtorAcctRows.length}):#|ACCT|NAME|TOTAL|OVD60|180D|HOLD|CRLIMIT|LASTPAY|LASTAMT`);
    debtorAcctRows.forEach((a,i) => {
      const hold = a['[OnHold]']==='Y'?'Y':'N';
      const pay  = a['[LastPay]']?.split('T')[0]||'-';
      lines.push(`${i+1}|${a['[Account]']}|${S(a['[Name]'])}|${C(a['[Total]'])}|${C(a['[Overdue60]'])}|${C(a['[Days180]'])}|${hold}|${C(a['[CrLimit]'])}|${pay}|${C(a['[LastAmt]'])}`);
    });

    // Slow movers: #|CODE|DESC|STOCK_VAL|QTY_OH
    if (botProdRows.length > 0) {
      lines.push(`SLOW_MOVERS(${botProdRows.length}):#|CODE|DESC|STOCK_VAL|QTY_OH`);
      botProdRows.forEach((s,i) => {
        lines.push(`${i+1}|${s['Stock[StockCode]']}|${S(s['Stock[Description]']||s['Stock[StockCode]'])}|${C(s['[StockVal]'])}|${Nn(s['[TotalOnHand]'])}`);
      });
    }

    return lines.join('\n');
  } catch(e) {
    return 'Live data unavailable: ' + e.message;
  }
}


// ── Call Claude via dataApi proxy ───────────────────────────────
async function askClaude(messages, contextData) {
  try {
    const token = await auth.currentUser?.getIdToken();
    const res = await fetch(`${API_BASE}/advisor`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ messages, context: contextData }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.reply;
  } catch (e) {
    throw e;
  }
}

// ── Message bubble ──────────────────────────────────────────────
function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 16,
      animation: 'fadeUp 0.3s ease both',
    }}>
      {!isUser && (
        <div style={{
          width: 28, height: 28, borderRadius: 6, flexShrink: 0,
          background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800, color: 'var(--void)',
          fontFamily: 'var(--font-body)', marginRight: 10, marginTop: 2,
          transform: 'rotate(45deg)',
        }}>
          <span style={{ transform: 'rotate(-45deg)' }}>IP</span>
        </div>
      )}
      <div style={{
        maxWidth: '72%',
        background: isUser ? 'var(--void)' : 'var(--card)',
        border: isUser ? '1px solid var(--border)' : '1px solid var(--card-border)',
        borderRadius: isUser ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
        padding: '12px 16px',
        boxShadow: isUser ? 'none' : '0 1px 8px rgba(0,0,0,0.04)',
      }}>
        {msg.role === 'loading' ? (
          <div style={{ display: 'flex', gap: 5, alignItems: 'center', padding: '2px 0' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: '50%',
                background: 'var(--gold)', opacity: 0.6,
                animation: `bounce 1.2s ease ${i * 0.2}s infinite`,
              }} />
            ))}
          </div>
        ) : (
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.65,
            color: isUser ? 'var(--text-primary)' : 'var(--page-text)',
            whiteSpace: 'pre-wrap',
          }} dangerouslySetInnerHTML={{ __html: msg.content
            .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
            .replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')
            .replace(/\*(.+?)\*/g,'<em>$1</em>')
            .replace(/^- (.+)/gm,'<span style="display:block;padding-left:12px">• $1</span>')
          }} />
        )}
        {msg.timestamp && (
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11,
            color: isUser ? 'var(--text-muted)' : 'var(--page-muted)',
            marginTop: 6, textAlign: isUser ? 'right' : 'left',
          }}>
            {new Date(msg.timestamp).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────
export default function Advisor() {
  const { period } = useOutletContext();
  const [messages,    setMessages]    = useState([]);
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [contextData, setContextData] = useState('');
  const [contextReady, setContextReady] = useState(false);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);

  // Load live context on mount / period change
  useEffect(() => {
    setContextReady(false);
    buildContext(period).then(ctx => {
      setContextData(ctx);
      setContextReady(true);
    });
  }, [period]);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const userText = text || input.trim();
    if (!userText || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: userText, timestamp: Date.now() };
    const loadingMsg = { role: 'loading', content: '' };

    setMessages(prev => [...prev, userMsg, loadingMsg]);
    setLoading(true);

    // Build history for Claude (exclude loading bubbles)
    const history = [...messages, userMsg].map(m => ({
      role: m.role === 'assistant' ? 'assistant' : 'user',
      content: m.content,
    }));

    try {
      const reply = await askClaude(history, contextData);
      setMessages(prev => [
        ...prev.filter(m => m.role !== 'loading'),
        { role: 'assistant', content: reply, timestamp: Date.now() },
      ]);
    } catch (e) {
      setMessages(prev => [
        ...prev.filter(m => m.role !== 'loading'),
        { role: 'assistant', content: `Sorry, I ran into an issue: ${e.message}. Please try again.`, timestamp: Date.now() },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const isEmpty = messages.length === 0;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - var(--topbar-h) - 36px)',
      background: 'var(--page)', borderRadius: 8, overflow: 'hidden',
      border: '1px solid var(--card-border)',
    }}>

      {/* ── Header ── */}
      <div style={{
        padding: '14px 20px', background: 'var(--card)',
        borderBottom: '1px solid var(--card-border)',
        display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 7,
          background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transform: 'rotate(45deg)', flexShrink: 0,
        }}>
          <span style={{ transform: 'rotate(-45deg)', fontSize: 11, fontWeight: 800, color: 'var(--void)', fontFamily: 'var(--font-body)' }}>IP</span>
        </div>
        <div>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 14, color: 'var(--page-text)', lineHeight: 1 }}>
            AI Business Advisor
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', marginTop: 3, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {contextReady
              ? `Live ${period} data loaded · Powered by Claude`
              : 'Loading live data…'}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: contextReady ? 'var(--green-l)' : 'var(--amber)',
            boxShadow: contextReady ? '0 0 6px var(--green-l)' : 'none',
            transition: 'all 0.4s',
          }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {contextReady ? 'Ready' : 'Loading'}
          </span>
        </div>
      </div>

      {/* ── Messages ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 8px' }}>

        {isEmpty && (
          <div style={{ textAlign: 'center', paddingTop: 40, animation: 'fadeUp 0.5s ease both' }}>
            <div style={{
              width: 52, height: 52, borderRadius: 12, margin: '0 auto 16px',
              background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transform: 'rotate(45deg)',
            }}>
              <span style={{ transform: 'rotate(-45deg)', fontSize: 18, fontWeight: 800, color: 'var(--void)', fontFamily: 'var(--font-body)' }}>IP</span>
            </div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, color: 'var(--page-text)', marginBottom: 6 }}>
              Ask me anything about your business
            </div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text-sec)', marginBottom: 28 }}>
              I have live access to your {period} data — revenue, margins, stock, debtors & branches.
            </div>

            {/* Suggestion chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 560, margin: '0 auto' }}>
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => send(s)} style={{
                  padding: '8px 14px',
                  background: 'var(--card)', border: '1px solid var(--card-border)',
                  borderRadius: 20, cursor: 'pointer',
                  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text-sec)',
                  transition: 'all 0.15s', textAlign: 'left',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--page-text)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.color = 'var(--page-text-sec)'; }}
                >{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <Message key={i} msg={msg} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* ── Input ── */}
      <div style={{
        padding: '12px 16px', background: 'var(--card)',
        borderTop: '1px solid var(--card-border)', flexShrink: 0,
      }}>
        <div style={{
          display: 'flex', gap: 10, alignItems: 'flex-end',
          background: 'var(--page)', border: '1px solid var(--card-border)',
          borderRadius: 10, padding: '8px 12px',
          transition: 'border-color 0.15s',
        }}
          onFocusCapture={e => e.currentTarget.style.borderColor = 'var(--gold)'}
          onBlurCapture={e => e.currentTarget.style.borderColor = 'var(--card-border)'}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask about revenue, margins, stock, debtors, branches…"
            disabled={loading || !contextReady}
            rows={1}
            style={{
              flex: 1, border: 'none', outline: 'none', resize: 'none',
              background: 'transparent', fontFamily: 'var(--font-body)',
              fontSize: 14, color: 'var(--page-text)', lineHeight: 1.5,
              maxHeight: 120, overflowY: 'auto',
            }}
            onInput={e => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          <button
            onClick={() => send()}
            disabled={loading || !input.trim() || !contextReady}
            style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: input.trim() && !loading ? 'var(--gold)' : 'var(--card-border)',
              border: 'none', cursor: input.trim() && !loading ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={input.trim() && !loading ? 'var(--void)' : 'var(--page-muted)'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', textAlign: 'center', marginTop: 6 }}>
          Enter to send · Shift+Enter for new line · Responses based on live {period} data
        </div>
      </div>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes bounce { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
}
